import DataSource from "common/framework/DataSource";
import FileInputModel from "common/models/FileInputModel";
import FileInputState from "common/enums/FileInputState";
import functions from "common/helper/functions";

/**
 * This is a special datasource designed specifically for file upload use cases.
 *
 * @type {kendo.Observable}
 */
const FileInputDataSource = DataSource.extend({

    _updateInProgress: false,

    /**
     * @param options
     *  {
     *      autoSync: false,
     *       transport: {
     *          read: function({success}) - Optional
     *           create: {
     *               url,
     *               method,
     *               parameterMap - Optional,
     *               dataType - Optinal
     *           },
     *           destroy: {
     *               event - FORMS ONLY. Do not use.
     *           }
     *       },
     *      schema: {
     *        parse - Optional. If specified must be a function that returns {id} object. Will be passed the network response.
     *      }
     *   }
     */
    init: function(options) {
        this._parseReponse = options?.schema?.parse ?? (result => result);
        const read = options?.transport?.read ?? ((readOpts) => readOpts.success([]));
        DataSource.fn.init.call(this, {
            batch: false,
            autoSync: false,
            transport: {
                read,
                destroy: destroyOpts => {
                    if(options?.transport?.destroy?.event) {
                        // This is a pretty terrible interface. DO NOT USE IT and DO NOT CHANGE IT.
                        // It exists because forms is a nightmare to modify and old DFUpload worked like this.
                        const file = this._destroyed.find(f => f.id === destroyOpts.data.id);
                        const promise = $.Deferred();
                        this.trigger(options.transport.destroy.event, {file, promise});
                        promise.done(() => destroyOpts.success({}));
                        promise.fail(() => destroyOpts.error({}));
                    } else {
                        destroyOpts.success({});
                    }
                },
                create: createOpts => {
                    const rawData = createOpts.data
                    const rawUrl = options.transport.create.url;
                    const url = typeof rawUrl === "function" ? rawUrl(rawData) : rawUrl;
                    const file = rawData.file;
                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("json", "1");
                    const extraFiles = rawData.extraFiles || [];
                    extraFiles.forEach((f, i) => {
                        formData.append(`extraFile${i}`, f);
                    })
                    if(options.transport.parameterMap) {
                        const data = options.transport.parameterMap(createOpts.data, "create");
                        Object.keys(data).forEach(key => formData.append(key, data[key]))
                    }
                    const model = this.data().find(f => f.file === file);
                    this.trigger("uploadStarted", {file: model}); //Forms and system transfer event
                    $.ajax({
                        url,
                        method: options.transport.create.method,
                        dataType: options.transport.create.dataType,
                        cache: false,
                        contentType: false,
                        processData: false,
                        data: formData,
                        success: response => {
                            //Can't return values in success callback because kendo ignores them if only SOME succeed
                            this._updateFileWithSuccess(file, response);
                            createOpts.success({});
                            this.trigger("uploadSuccess", {file: model, response}); //Forms and system transfer event
                        },
                        error: (xhr) => {
                            this._updateFileWithError(file, "");
                            createOpts.error();
                            this.trigger("uploadError", {file: model, response: xhr?.responseJSON || {}}); //Forms and system transfer event
                        }
                    }).handle("error/message", e => {
                        this._updateFileWithError(file, e.get("message"));
                    }).handle("error/server", e => {
                        this._updateFileWithError(file, e.get("message") || e.get("error"));
                    });
                }
            },
            schema: {
                model: FileInputModel
            }
        });
        if(options.autoSync) {
            //The built-in kendo autoSync behavior has many problems with error cases and changing items.
            //This works around all of that.
            const doAutoSync = functions.debounce(this.sync, 0, this);
            this.bind("change", function(e) {
                if(["add", "remove"].includes(e?.action)) {
                    doAutoSync();
                }
            });
        }
    },

    _updateFileWithSuccess(file, response) {
        this.data().filter(f => f.file === file).forEach(f => {
            const parsed = this._parseReponse(response);
            f.set("state", FileInputState.SUCCESS);
            f.set("errorMessage", "");
            f.set("id", parsed?.id || kendo.guid());
            f.set("width", parsed?.width || f.width);
            f.set("height", parsed?.height || f.height);
            f.set("canDelete", !!(parsed?.canDelete));
            f.dirty = false;
        });
    },

    _updateFileWithError(file, message) {
        this.data().filter(f => f.file === file).forEach(f => {
            f.set("state", FileInputState.ERROR);
            f.set("errorMessage", message);
        });
    },

    addFile(file) {
        const model = new FileInputModel({
            name: file.name,
            size: file.size,
            state: FileInputState.READY,
            errorMessage: null,
            canView: false,
            canDelete: false,
            file: null
        });
        model.file = file;
        this.add(model);
    },

    addFileWithExtra(file, name, extraFiles) {
        const size = extraFiles.reduce((size, f) => size + f.size, file.size);
        const model = new FileInputModel({
            name: name,
            size: size,
            state: FileInputState.READY,
            errorMessage: null,
            canView: false,
            canDelete: false,
            file: null
        });
        model.file = file;
        model.extraFiles = extraFiles;
        this.add(model);
    },

    addFiles(files) {
        for(let i = 0; i < files.length; i++) {
            this.addFile(files[i]);
        }
    }

});
export default FileInputDataSource;
